import { Button, Text } from '@react-spectrum/s2';
import AlertTriangle from '@react-spectrum/s2/icons/AlertTriangle';
import CheckmarkCircle from '@react-spectrum/s2/icons/CheckmarkCircle';
import CloseCircle from '@react-spectrum/s2/icons/CloseCircle';
import {
	iconStyle,
	style,
} from '@react-spectrum/s2/style' with { type: 'macro' };
import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
	clearUrlHash,
	exchangeForLongLivedToken,
	extractAccessTokenFromHash,
	FacebookApiError,
	type FacebookPage,
	fetchFacebookAccounts,
	type LongLivedTokenResponse,
	redirectToFacebookOAuth,
	REQUIRED_INSTAGRAM_SCOPES,
} from '@/api/FacebookApi';
import { logger } from '@/logger';
import {
	type InstagramConnectFailureDetails,
	type InstagramConnectFailureStep,
	OAUTH_MESSAGE_TYPE,
	type OAuthErrorMessage,
	type OAuthSuccessMessage,
} from '@/spaces/settings/oauthMessages';
import type { InstagramConnection } from '@/spaces/settings/userSettingsTypes';

import { InstagramAccountSelect } from './InstagramAccountSelect.tsx';
import { InstagramLogin } from './InstagramLogin.tsx';

type ConnectFailure = {
	step: InstagramConnectFailureStep;
	message: string;
	details?: InstagramConnectFailureDetails;
};

type AuthState =
	| { state: 'loading' }
	| { state: 'login' }
	| {
			state: 'accountSelection';
			pages: FacebookPage[];
			longUser: LongLivedTokenResponse;
			grantedScopes: string[] | null;
	  }
	| {
			state: 'submittingSelection';
			pages: FacebookPage[];
			longUser: LongLivedTokenResponse;
			grantedScopes: string[] | null;
	  }
	| { state: 'done'; missingScopes: string[] }
	| { state: 'failed'; failure: ConnectFailure };

// A heading that names the blocker. The old single "Something went wrong"
// heading told a customer nothing, so they quoted it into support chat.
const FAILURE_HEADINGS: Record<InstagramConnectFailureStep, string> = {
	oauth_redirect: 'Facebook did not complete the connection',
	token_exchange: 'We could not finish signing in with Facebook',
	fetch_pages: 'We could not read your Facebook Pages',
	no_instagram_pages: 'No Instagram Business account found',
	page_not_found: 'That Facebook Page is no longer available',
	page_missing_instagram: 'That Facebook Page has no Instagram account linked',
	connection_build: 'We could not finish the connection',
};

// Meta writes the failure text, and anyone can put text in the error_description
// query parameter of a crafted /auth/instagram link. Bound the size here so an
// enormous string cannot travel on to the opener, the log, or chat state. This
// is a size bound only: the reader of this payload owns its own safety.
const MAX_FAILURE_TEXT_CHARS = 300;

const clampText = (value: string): string =>
	value.replace(/\s+/g, ' ').trim().slice(0, MAX_FAILURE_TEXT_CHARS);

const clampFailure = (failure: ConnectFailure): ConnectFailure => {
	if (!failure.details) {
		return { step: failure.step, message: clampText(failure.message) };
	}
	const details: InstagramConnectFailureDetails = {};
	for (const [key, value] of Object.entries(failure.details)) {
		details[key] = typeof value === 'string' ? clampText(value) : value;
	}
	return { step: failure.step, message: clampText(failure.message), details };
};

// Meta's permission names mean nothing to a customer, so name what each one
// lets Beacon do instead.
const SCOPE_LABELS: Record<string, string> = {
	instagram_manage_insights: 'View your Instagram insights',
};

const describeScope = (scope: string): string => SCOPE_LABELS[scope] ?? scope;

const sendSuccess = (connectionData: InstagramConnection) => {
	if (!window.opener) return;
	const payload: OAuthSuccessMessage = {
		type: OAUTH_MESSAGE_TYPE,
		provider: 'instagram',
		status: 'success',
		connectionUpdated: true,
		connectionData,
	};
	window.opener.postMessage(payload, window.location.origin);
};

const sendFailure = (failure: ConnectFailure) => {
	if (!window.opener) return;
	const payload: OAuthErrorMessage = {
		type: OAUTH_MESSAGE_TYPE,
		provider: 'instagram',
		status: 'error',
		code: failure.step,
		message: failure.message,
		...(failure.details && { details: failure.details }),
	};
	window.opener.postMessage(payload, window.location.origin);
};

export const AuthInstagram = () => {
	const [authState, setAuthState] = useState<AuthState>({ state: 'loading' });
	const hasStartedRef = useRef(false);

	const handleSuccess = useCallback(
		(connectionData: InstagramConnection, missingScopes: string[]) => {
			setAuthState({ state: 'done', missingScopes });
			sendSuccess(connectionData);
			// The customer has to read the missing-permission warning and act on
			// it, so leave the window open when there is one.
			if (missingScopes.length > 0) return;
			setTimeout(() => {
				window.close();
			}, 2000);
		},
		[],
	);

	const fail = useCallback((rawFailure: ConnectFailure) => {
		const failure = clampFailure(rawFailure);
		setAuthState({ state: 'failed', failure });
		// Name each field. The raw error bodies hold access tokens, and a token in
		// a browser log line is the leak this flow already caused once.
		logger.error(`Instagram connect failed: ${failure.step}`, {
			...failure.details,
			message: failure.message,
		});
		// These errors are all caught, so Sentry sees nothing without this call.
		// The step is the exception name, so the failures group by cause.
		Sentry.captureException(
			Object.assign(new Error(failure.message), {
				name: `InstagramConnectFailed: ${failure.step}`,
			}),
			{ extra: { step: failure.step, ...failure.details } },
		);
		sendFailure(failure);
	}, []);

	const handleConfirmSelection = useCallback(
		async (selectedPageId: string) => {
			if (
				authState.state !== 'accountSelection' &&
				authState.state !== 'submittingSelection'
			) {
				return;
			}
			const { pages, longUser, grantedScopes } = authState;
			const selectedPage = pages.find((page) => page.id === selectedPageId);
			if (!selectedPage) {
				fail({ step: 'page_not_found', message: 'Selected page not found' });
				return;
			}

			const igAccount =
				selectedPage.connected_instagram_account ??
				(selectedPage.instagram_business_account
					? {
							id: selectedPage.instagram_business_account.id,
							username: 'instagram',
						}
					: null);
			if (!igAccount) {
				fail({
					step: 'page_missing_instagram',
					message: 'Selected page does not have a connected Instagram account',
				});
				return;
			}

			setAuthState({
				state: 'submittingSelection',
				pages,
				longUser,
				grantedScopes,
			});

			try {
				const igTokenExpiresAt =
					!longUser.expires_at || longUser.expires_at === 0
						? null
						: new Date(longUser.expires_at * 1000).toISOString();
				const dataAccessExpiresAt = longUser.data_access_expires_at
					? new Date(longUser.data_access_expires_at * 1000).toISOString()
					: undefined;

				const connectionData: InstagramConnection = {
					ig_user_id: igAccount.id,
					page_id: selectedPage.id,
					page_token: selectedPage.access_token,
					ig_token_expires_at: igTokenExpiresAt,
					ig_username:
						'username' in igAccount ? igAccount.username : 'instagram',
					ig_profile_picture_url:
						'profile_picture_url' in igAccount
							? (igAccount.profile_picture_url ?? null)
							: null,
					connected_at: new Date().toISOString(),
					...(dataAccessExpiresAt && {
						data_access_expires_at: dataAccessExpiresAt,
					}),
					// Store the full granted list, not the required subset: the whole
					// list is what makes a later support question answerable.
					...(grantedScopes && { granted_scopes: grantedScopes }),
				};

				// A null grant means Meta told us nothing, so we cannot claim a
				// permission is missing.
				const missingScopes =
					grantedScopes === null
						? []
						: REQUIRED_INSTAGRAM_SCOPES.filter(
								(scope) => !grantedScopes.includes(scope),
							);

				handleSuccess(connectionData, missingScopes);
			} catch (err) {
				fail({
					step: 'connection_build',
					message:
						err instanceof Error
							? err.message
							: 'Failed to complete Instagram authentication',
				});
			}
		},
		[authState, fail, handleSuccess],
	);

	const startOAuth = useCallback(() => {
		redirectToFacebookOAuth();
	}, []);

	const initializeAuth = useCallback(async () => {
		// The stage this run has reached. One catch covers both Meta calls, and
		// only a `!response.ok` body becomes a FacebookApiError carrying its own
		// step. A rejected fetch throws a bare TypeError, and a non-JSON error
		// body throws a SyntaxError, so neither names its step. Assuming
		// `token_exchange` for those told a customer whose page fetch died that
		// sign-in had failed, and the agent's guidance keys off the step, so it
		// asked them to sign out of Facebook and start again (ADO-1943).
		let stage: InstagramConnectFailureStep = 'token_exchange';
		try {
			const { accessToken, error, grantedScopes } =
				extractAccessTokenFromHash();
			if (error) {
				fail({ step: 'oauth_redirect', message: error });
				return;
			}

			if (!accessToken) {
				setAuthState({ state: 'login' });
				return;
			}

			clearUrlHash();

			const longUser = await exchangeForLongLivedToken(accessToken);
			// The exchange returned, so nothing after this line can be a
			// token_exchange failure.
			stage = 'fetch_pages';
			const accounts = await fetchFacebookAccounts(longUser.access_token);
			const pagesWithInstagram = accounts.data.filter(
				(page) =>
					page.connected_instagram_account || page.instagram_business_account,
			);
			if (pagesWithInstagram.length === 0) {
				// This failure carries no Meta error object, so the page inventory is
				// the only evidence of why nothing matched.
				fail({
					step: 'no_instagram_pages',
					message:
						'No Instagram Business accounts found. Please connect Instagram to a Facebook Page first.',
					details: {
						page_count: accounts.data.length,
						pages_with_connected_instagram: accounts.data.filter(
							(page) => page.connected_instagram_account,
						).length,
						pages_with_instagram_business_account: accounts.data.filter(
							(page) => page.instagram_business_account,
						).length,
					},
				});
				return;
			}
			setAuthState({
				state: 'accountSelection',
				pages: pagesWithInstagram,
				longUser,
				grantedScopes,
			});
		} catch (err) {
			const details: InstagramConnectFailureDetails = {};
			if (err instanceof FacebookApiError) {
				details.status = err.status;
				if (err.metaCode !== undefined) details.meta_code = err.metaCode;
				if (err.metaSubcode !== undefined)
					details.meta_subcode = err.metaSubcode;
				if (err.metaType !== undefined) details.meta_type = err.metaType;
				if (err.fbtraceId !== undefined) details.fbtrace_id = err.fbtraceId;
				if (err.metaMessage !== undefined)
					details.meta_message = err.metaMessage;
			}
			fail({
				step: err instanceof FacebookApiError ? err.step : stage,
				message:
					err instanceof Error
						? err.message
						: 'Failed to initialize Instagram auth',
				...(Object.keys(details).length > 0 && { details }),
			});
		}
	}, [fail]);

	useEffect(() => {
		if (hasStartedRef.current) return;
		hasStartedRef.current = true;
		void initializeAuth();
	}, [initializeAuth]);

	return (
		<div className="inset-0 flex h-screen w-screen items-center justify-center">
			<div
				className="my-6 flex h-1/2 w-full max-w-4/5 flex-col items-center
					justify-center gap-4 rounded-xl border border-(--glass-border)
					bg-(--glass-bg) p-6 text-center"
			>
				{authState.state === 'loading' && (
					<div className="flex flex-col gap-4">
						<Text
							styles={style({
								color: 'heading',
								fontSize: 'heading-lg',
								fontWeight: 'bold',
							})}
						>
							Connecting to Instagram
						</Text>
						<Text styles={style({ color: 'title', fontSize: 'title-xl' })}>
							Please wait...
						</Text>
					</div>
				)}
				{authState.state === 'login' && (
					<InstagramLogin onConnect={startOAuth} />
				)}
				{(authState.state === 'accountSelection' ||
					authState.state === 'submittingSelection') && (
					<InstagramAccountSelect
						pages={authState.pages}
						onConfirm={(pageId) => void handleConfirmSelection(pageId)}
						isSubmitting={authState.state === 'submittingSelection'}
					/>
				)}
				{authState.state === 'done' && authState.missingScopes.length === 0 && (
					<div className="flex flex-col gap-4">
						<div className="flex items-center justify-center gap-2">
							<CheckmarkCircle
								styles={iconStyle({ size: 'XL', color: 'positive' })}
							/>
							<Text
								styles={style({
									color: 'heading',
									fontSize: 'heading-lg',
									fontWeight: 'bold',
								})}
							>
								Success!
							</Text>
						</div>
						<Text
							UNSAFE_className="whitespace-pre-line"
							styles={style({ color: 'title', fontSize: 'title-xl' })}
						>
							{`Instagram account connected successfully.
This window will close automatically...`}
						</Text>
					</div>
				)}
				{authState.state === 'done' && authState.missingScopes.length > 0 && (
					<div className="flex flex-col gap-4">
						<div className="flex items-center justify-center gap-2">
							<AlertTriangle
								styles={iconStyle({ size: 'XL', color: 'notice' })}
							/>
							<Text
								styles={style({
									color: 'heading',
									fontSize: 'heading-lg',
									fontWeight: 'bold',
								})}
							>
								Some permissions are missing
							</Text>
						</div>
						<Text
							UNSAFE_className="whitespace-pre-line"
							styles={style({ color: 'title', fontSize: 'title-xl' })}
						>
							{`Your Instagram account is connected, but these permissions were not approved:

${authState.missingScopes.map((scope) => `• ${describeScope(scope)}`).join('\n')}

Beacon needs them to work. Connect again and approve every permission on the Facebook screen.`}
						</Text>
						<Button variant="accent" onPress={startOAuth}>
							<Text>Connect again</Text>
						</Button>
					</div>
				)}
				{authState.state === 'failed' && (
					<div className="flex flex-col gap-4">
						<div className="flex items-center justify-center gap-2">
							<CloseCircle
								styles={iconStyle({ size: 'XL', color: 'negative' })}
							/>
							<Text
								styles={style({
									color: 'heading',
									fontSize: 'heading-lg',
									fontWeight: 'bold',
								})}
							>
								{FAILURE_HEADINGS[authState.failure.step]}
							</Text>
						</div>
						<Text
							UNSAFE_className="whitespace-pre-line"
							styles={style({ color: 'title', fontSize: 'title-xl' })}
						>
							{authState.failure.message}
						</Text>
						<Button variant="accent" onPress={startOAuth}>
							<Text>Try again</Text>
						</Button>
					</div>
				)}
			</div>
		</div>
	);
};
